import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Users,
  Plus,
  Edit,
  Trash2,
  UserCog,
  Check,
  X,
  Search,
  Lock,
  Unlock,
  Crown,
  ShoppingBag,
  Wrench,
  User,
  Briefcase,
  Eye,
  LayoutDashboard,
  Settings,
  FileText,
  AlertTriangle,
  Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navigation for Platform Admin
const platformAdminNavItems = [
  { href: "/admin", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
  { href: "/admin/workers", label: "Workers", icon: <Users className="w-5 h-5" /> },
  { href: "/admin/roles", label: "Role Management", icon: <Shield className="w-5 h-5" /> },
  { href: "/admin/webshop", label: "Webshop", icon: <ShoppingBag className="w-5 h-5" /> },
  { href: "/admin/rentals", label: "Rentals", icon: <Wrench className="w-5 h-5" /> },
  { href: "/projects", label: "All Projects", icon: <Briefcase className="w-5 h-5" /> },
  { href: "/admin/settings", label: "Settings", icon: <Settings className="w-5 h-5" /> },
];

const ROLE_ICONS = {
  platform_admin: <Crown className="w-4 h-4 text-yellow-400" />,
  holding_admin: <Building2 className="w-4 h-4 text-emerald-400" />,
  operations_manager: <UserCog className="w-4 h-4 text-blue-400" />,
  webshop_admin: <ShoppingBag className="w-4 h-4 text-purple-400" />,
  rentals_admin: <Wrench className="w-4 h-4 text-orange-400" />,
  team_leader: <Users className="w-4 h-4 text-green-400" />,
  worker: <User className="w-4 h-4 text-cyan-400" />,
  client_admin: <Briefcase className="w-4 h-4 text-pink-400" />,
  client_viewer: <Eye className="w-4 h-4 text-gray-400" />,
};

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Role dialog
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    role_id: "",
    name: "",
    description: "",
    permissions: []
  });
  
  // User role dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserRole, setNewUserRole] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, usersRes, permsRes] = await Promise.all([
        axios.get(`${API}/admin/roles`),
        axios.get(`${API}/workers`),
        axios.get(`${API}/admin/available-permissions`)
      ]);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
      setPermissions(permsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permId) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const saveRole = async () => {
    if (!roleForm.name || !roleForm.role_id) {
      toast.error("Role ID and name are required");
      return;
    }

    try {
      if (editingRole) {
        await axios.put(`${API}/admin/roles/${editingRole.role_id}`, roleForm);
        toast.success("Role updated!");
      } else {
        await axios.post(`${API}/admin/roles`, roleForm);
        toast.success("Role created!");
      }
      setRoleDialogOpen(false);
      resetRoleForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save role");
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      role_id: role.role_id,
      name: role.name,
      description: role.description || "",
      permissions: role.permissions || [],
      is_default: role.is_default || false
    });
    setRoleDialogOpen(true);
  };

  const resetRoleForm = () => {
    setEditingRole(null);
    setRoleForm({
      role_id: "",
      name: "",
      description: "",
      permissions: []
    });
  };

  const openUserRoleDialog = (user) => {
    setSelectedUser(user);
    setNewUserRole(user.role);
    setUserDialogOpen(true);
  };

  const assignUserRole = async () => {
    if (!selectedUser || !newUserRole) return;

    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/role`, {
        role: newUserRole
      });
      toast.success(`Role changed to ${newUserRole.replace("_", " ")}`);
      setUserDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change role");
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout navItems={platformAdminNavItems} title="Role Management">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={platformAdminNavItems} title="Role Management">
      <div className="space-y-6">
        {/* Roles Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#23F7DD]" />
              Available Roles
            </CardTitle>
            <Dialog open={roleDialogOpen} onOpenChange={(open) => { setRoleDialogOpen(open); if (!open) resetRoleForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Role
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Unbounded']">
                    {editingRole ? "Edit Role" : "Create Custom Role"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Role ID *</label>
                      <Input
                        value={roleForm.role_id}
                        onChange={(e) => setRoleForm(prev => ({ ...prev, role_id: e.target.value.toLowerCase().replace(/\s/g, "_") }))}
                        placeholder="e.g., inventory_manager"
                        className="bg-black/50 border-white/10 text-white"
                        disabled={editingRole}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Display Name *</label>
                      <Input
                        value={roleForm.name}
                        onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Inventory Manager"
                        className="bg-black/50 border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Description</label>
                    <Textarea
                      value={roleForm.description}
                      onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what this role can do..."
                      className="bg-black/50 border-white/10 text-white"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-2 block">Permissions</label>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 bg-black/30 rounded-lg">
                      {permissions.map((perm) => (
                        <button
                          key={perm.id}
                          onClick={() => togglePermission(perm.id)}
                          className={`flex items-center gap-2 p-2 rounded-lg border transition-colors text-left ${
                            roleForm.permissions.includes(perm.id)
                              ? "bg-[#23F7DD]/20 border-[#23F7DD] text-white"
                              : "border-white/10 text-[#94A3B8] hover:border-white/30"
                          }`}
                        >
                          {roleForm.permissions.includes(perm.id) ? (
                            <Check className="w-4 h-4 text-[#23F7DD] flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 opacity-50 flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{perm.name}</p>
                            <p className="text-xs opacity-70">{perm.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={saveRole}
                    className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                  >
                    {editingRole ? "Update Role" : "Create Role"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {roles.map((role) => (
                <div
                  key={role.role_id}
                  className={`p-4 rounded-lg border transition-colors hover:border-[#23F7DD]/50 cursor-pointer ${
                    role.is_default
                      ? "bg-gradient-to-br from-white/5 to-white/0 border-white/10"
                      : "bg-[#23F7DD]/5 border-[#23F7DD]/30"
                  }`}
                  onClick={() => openEditRole(role)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {ROLE_ICONS[role.role_id] || <Shield className="w-4 h-4 text-[#94A3B8]" />}
                      <h4 className="text-white font-medium">{role.name}</h4>
                    </div>
                    <div className="flex items-center gap-1">
                      {role.is_default && (
                        <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-500">
                          Default
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openEditRole(role); }}
                        className="text-[#94A3B8] hover:text-[#23F7DD]"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-[#94A3B8] mt-2">{role.description}</p>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {role.permissions?.slice(0, 4).map((perm) => (
                      <Badge key={perm} variant="outline" className="text-xs border-white/20 text-[#94A3B8]">
                        {perm === "*" ? "All" : perm}
                      </Badge>
                    ))}
                    {role.permissions?.length > 4 && (
                      <Badge variant="outline" className="text-xs border-white/20 text-[#94A3B8]">
                        +{role.permissions.length - 4} more
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Users Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#23F7DD]" />
              User Roles
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="pl-9 bg-black/50 border-white/10 text-white"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <div
                  key={user.user_id}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#23F7DD]/30 to-[#4F46E5]/30 flex items-center justify-center">
                      {ROLE_ICONS[user.role] || <User className="w-5 h-5 text-white" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-sm text-[#94A3B8]">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge 
                      className={`${
                        user.role === "platform_admin" ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" :
                        user.role === "webshop_admin" ? "bg-purple-500/20 text-purple-400 border-purple-500/30" :
                        user.role === "rentals_admin" ? "bg-orange-500/20 text-orange-400 border-orange-500/30" :
                        user.role === "team_leader" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                        "bg-white/10 text-[#94A3B8] border-white/20"
                      }`}
                    >
                      {user.role?.replace("_", " ")}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openUserRoleDialog(user)}
                      className="text-[#94A3B8] hover:text-[#23F7DD]"
                    >
                      <UserCog className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Change User Role Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded']">Change User Role</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-white font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-[#94A3B8]">{selectedUser.email}</p>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    Current role: <span className="text-[#23F7DD]">{selectedUser.role?.replace("_", " ")}</span>
                  </p>
                </div>
                
                {/* Warning when changing worker to non-worker role */}
                {(selectedUser.role === 'worker' || selectedUser.role === 'team_leader') && 
                 newUserRole && 
                 !['worker', 'team_leader'].includes(newUserRole) && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium">Atenție!</p>
                        <p className="text-sm text-yellow-300/80">
                          Schimbând rolul din {selectedUser.role === 'team_leader' ? 'Team Leader' : 'Worker'} în {newUserRole?.replace('_', ' ')}, 
                          acest utilizator va dispărea din pagina Workers. Profilul său de worker va fi păstrat în baza de date.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-sm text-[#94A3B8] mb-2 block">Select New Role</label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {roles.map((role) => (
                      <button
                        key={role.role_id}
                        onClick={() => setNewUserRole(role.role_id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                          newUserRole === role.role_id
                            ? "bg-[#23F7DD]/20 border-[#23F7DD]"
                            : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        {ROLE_ICONS[role.role_id] || <Shield className="w-4 h-4 text-[#94A3B8]" />}
                        <div>
                          <p className="text-white font-medium">{role.name}</p>
                          <p className="text-xs text-[#94A3B8]">{role.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={assignUserRole}
                  disabled={newUserRole === selectedUser.role}
                  className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                >
                  Assign Role
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
